---
title: "Market Sentiment: Fear & Greed, Funding Rates, Open Interest"
slug: market-sentiment
shortTitle: "Market Sentiment"
description: "What fear and greed indexes, perpetual funding rates, open interest and client long/short data tell you about crypto positioning, and why none of them is a timing signal."
level: advanced
category: crypto
course: advanced
order: 11
topics: [bitcoin, altcoins, psychology]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/market-sentiment.svg
faqs:
  - q: "Does {{BRAND}} charge funding rates?"
    a: "No. Funding rates belong to perpetual futures on crypto exchanges. CFDs on {{BRAND}} use a daily swap instead, worked out from the position value and the market's long or short swap rate."
  - q: "What does a high positive funding rate mean?"
    a: "It means traders holding long perpetual futures are paying those holding shorts, usually because demand for leveraged longs has pushed the perpetual price above the spot price. Many traders read very high funding as a sign that long positions are crowded."
  - q: "Does rising open interest mean the price will rise?"
    a: "No. Every futures contract has a buyer and a seller, so open interest shows how much money is committed, not which side will win. It is usually read together with the price trend and funding."
  - q: "What does the client sentiment figure on {{BRAND}} show?"
    a: "It shows the share of traders holding a market on live {{BRAND}} accounts who are long. It counts traders rather than money, appears once enough traders hold the market, and describes positioning, not where the price will go."
  - q: "Should I trade against the crowd?"
    a: "Contrarian thinking treats a crowded side as a reason for caution, but crowds are often right while a trend lasts. Many traders use sentiment to adjust their risk rather than as a reason to open a trade."
---

Prices move because traders buy and sell, so how those traders feel and where they have placed their money matters. Crypto publishes an unusually rich set of data on both. **Fear and greed indexes** try to score the mood. **Funding rates** show who is paying to hold leveraged positions on perpetual futures. **Open interest** measures how much money is committed to those futures. All of this comes from the wider crypto market and data providers, not from {{BRAND}}.

{{BRAND}} adds its own reading: the share of its clients who are long a market. This lesson explains what each measure tells you, how traders tend to read them, and why sentiment works better as a risk check than as a reason to trade.

<CTA />

<KeyTakeaways>

- **Sentiment describes positioning, not direction.** It tells you how traders feel or are placed today, not where the price goes tomorrow.
- **Funding shows who pays to hold leverage.** High positive funding means leveraged longs are paying shorts; it is a perpetual-futures concept, not a {{BRAND}} cost.
- **Open interest shows how much is at stake.** It counts open contracts, each with a buyer and a seller, so it never tells you which side is right.
- **Extremes are a warning light, not a trigger.** Crowded markets can stay crowded; use extremes to question your size and stop.

</KeyTakeaways>

## Mood versus positioning

Sentiment data falls into two groups. **Mood** measures, such as surveys and composite indexes, try to capture how optimistic or fearful traders feel. **Positioning** measures, such as funding, open interest and long/short ratios, show where money actually sits.

The classic way to use both is **contrarian**: when nearly everyone is already long, fewer new buyers are left, and if the price turns, many holders may rush to exit at once. The catch is that crowds are often right for long stretches while a trend runs. Sentiment works best as context, read alongside price structure and your own plan. [Trading psychology basics](/academy/article/trading-psychology-basics) covers the fear, greed and FOMO behind these numbers.

## Fear and greed indexes

A **fear and greed index** is a score, usually from 0 to 100, built by a data provider from inputs such as recent volatility, price momentum, trading volume, social media activity, surveys and Bitcoin's share of the total crypto market. Low readings are labelled fear, high readings greed.

Extreme readings have sometimes appeared near turning points, and have also lasted for weeks while the trend continued. Each provider weighs its inputs differently, and because much of the input is recent price action, the index partly restates what the chart already shows.

## Funding rates

Crypto exchanges offer **perpetual futures** ("perps"): futures contracts with no expiry date. To keep a perp's price close to the spot price of the coin, exchanges use **funding**: regular payments between traders holding longs and traders holding shorts, often every eight hours, though schedules vary.

- **Positive funding:** longs pay shorts. The perp is trading above spot, usually because demand for leveraged longs is strong.
- **Negative funding:** shorts pay longs. Demand for leveraged shorts is pushing the perp below spot.

<Example title="What funding costs a perp trader (illustrative rates)">

A trader holds a 10,000 USDT long perp position on an exchange.

- At a funding rate of +0.01% every 8 hours: 10,000 × 0.0001 = **1 USDT** per payment, 3 USDT a day.
- At +0.10% every 8 hours: 10,000 × 0.001 = **10 USDT** per payment, 30 USDT a day: 0.3% of the position daily, or about 9% over 30 days.

When longs keep paying rates like the second one to stay in, many traders read it as a sign that leveraged longs are crowded.

</Example>

{{BRAND}} does not charge funding. Its CFDs use a daily **swap** charged at {{SWAP_HOUR}}, worked out as the position value × the market's long or short swap rate. Funding rates are useful market data to read from exchanges or data sites, not a cost on your {{BRAND}} account. [Trading costs, swaps and fees](/academy/article/trading-costs-swaps-fees) explains the difference in full.

## Open interest

**Open interest (OI)** is the total value, or number, of derivatives contracts that are still open. Every contract has a buyer and a seller, so OI does not tell you which side is crowded. It tells you how much leveraged money is committed, and that is fuel for sharp moves when positions are forced to close.

Traders usually read OI together with price. These are common interpretations, not rules:

- **Price up, OI up:** new positions are opening as the price rises; fresh money is behind the move.
- **Price up, OI down:** positions are closing, often shorts buying back; the rise may have fewer new buyers behind it.
- **Price down, OI up:** new positions are opening into the fall, often new shorts.
- **Price down, OI down:** positions are closing, often longs exiting or being liquidated.

When OI is high and funding is extreme, a modest move can trigger forced closures that push the price further and trigger more. These **liquidation cascades** can run in either direction.

The Markets page and each market's page on {{BRAND}} also show an **Open interest** figure. That one measures the value of open positions on {{BRAND}} only, a different and much smaller pool than exchange-wide futures open interest.

{/* TODO: verify with platform — the Open interest column and Market stats row cover open positions on this platform only. */}

## Client sentiment on {{BRAND}}

The **Sentiment** column on the [Markets](/markets) page, and the Sentiment card on each market's page, show the share of traders holding that market on live {{BRAND}} accounts who are long. It counts traders, not money, and a trader holding both a buy and a sell counts on both sides. It appears once enough traders hold the market; where available, a lighter **Wider market** reading shows how traders outside {{BRAND}} are positioned. The **Opportunity scanner** lists markets as **Crowded long** when at least 70% of their traders are long, and **Crowded short** when 30% or fewer are.

<Example title="Reading client sentiment (illustrative)">

50 traders hold SOL/USDT on live accounts: 38 have a buy open and 12 have a sell open. The long share is 38 ÷ 50 = **76%**, so the scanner would list it as Crowded long. That describes how these traders are placed. It does not say whether SOL rises or falls next, and {{BRAND}} clients do not set the price, which comes from external price feeds.

</Example>

{/* TODO: verify with platform — Sentiment column and card behavior, Wider market availability, and the Crowded long / Crowded short thresholds. */}

<CompareTable caption="Sentiment measures at a glance">

| Measure | What it shows | Where it comes from | Main caveat |
| --- | --- | --- | --- |
| Fear and greed index | A score of market mood | Third-party data providers | Partly restates recent price action |
| Funding rate | Who pays to hold leveraged perps | Crypto futures exchanges | Can stay extreme for weeks |
| Open interest | How much is committed to futures | Crypto futures exchanges | Says nothing about which side wins |
| Client sentiment | Share of {{BRAND}} traders who are long | {{BRAND}}, live accounts | A small, platform-only sample |

</CompareTable>

## Using sentiment without letting it trade for you

<Steps>
<Step title="Start from your own plan">

Mark your levels, direction and stop from price structure first. Sentiment comes second.

</Step>
<Step title="Check for extremes">

Look at funding, open interest, any fear and greed reading you follow, and the client Sentiment figure. Note anything unusually one-sided.

</Step>
<Step title="If you are on the crowded side, tighten risk">

Ask where the crowd's stops and liquidation levels might sit, and whether your stop sits in the same place. Consider a smaller size or less leverage rather than adding.

</Step>
<Step title="Wait for price to agree">

A contrarian idea needs the price to start turning before it becomes a trade. An extreme reading alone is not a signal.

</Step>
</Steps>

<Warning title="Crowds can stay crowded">

Selling a market because everyone is long, or buying because everyone is short, can mean stepping in front of a strong trend. Leverage magnifies those losses, and most CFD traders lose money. Size contrarian trades small, always with a stop.

</Warning>

<ProTip title="Use sentiment to size, not to pick a side">

The most practical use of sentiment is often defensive: trimming size, avoiding extra positions, or moving a stop out of an obvious cluster when a market looks crowded on your side.

</ProTip>

## Conclusion

Fear and greed indexes score mood; funding rates show who pays to hold leverage on perpetual futures; open interest shows how much is committed; client sentiment shows how {{BRAND}} traders are placed. All describe the present, none predicts the next move, and the first three come from the wider market rather than your {{BRAND}} account. Read them alongside price, treat extremes as a reason to check your risk, and let your own plan, not the crowd, decide your trades. For a refresher on how coins and stablecoins fit together, see [crypto basics](/academy/article/crypto-basics).
